import { useNotify, useResourceContext, useTranslate, useUpdate } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { OutreachList } from "../types";

export const OutreachListCard = ({
  list,
  activeCount,
}: {
  list: OutreachList;
  activeCount: number;
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const [update, { isPending }] = useUpdate();
  // "offer" or "channels" — whichever page rendered this card (dataProvider.ts
  // rewrites both to public.outreach_lists under the hood).
  const resource = useResourceContext();
  const isChannel = list.criteria?.list_kind === "channel";

  const handleToggle = (checked: boolean) => {
    update(
      resource,
      { id: list.id, data: { is_active: checked }, previousData: list },
      {
        onError: () =>
          notify("resources.outreach_lists.errors.toggle_failed", {
            type: "error",
            messageArgs: { _: "Failed to update list status" },
          }),
      },
    );
  };

  return (
    <Card>
      <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/${resource}/${list.id}/show`}
              className="font-semibold text-base text-foreground hover:underline"
            >
              {list.name}
            </Link>
            {/* Identity badge only makes sense on the Channel page — an
                Offer's identity column is legacy leftover from before the
                split, not this axis's concern. */}
            {isChannel && <Badge variant="secondary">{list.identity}</Badge>}
            {!list.is_active && <Badge variant="outline">Inactive</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{list.purpose}</p>
          <p className="text-sm mt-1">
            {translate("resources.outreach_lists.active_members_count", {
              smart_count: activeCount,
              _: `${activeCount} active member${activeCount === 1 ? "" : "s"}`,
            })}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label
            htmlFor={`outreach-list-active-${list.id}`}
            className="text-xs text-muted-foreground"
          >
            {list.is_active ? "Active" : "Inactive"}
          </Label>
          <Switch
            id={`outreach-list-active-${list.id}`}
            checked={list.is_active}
            onCheckedChange={handleToggle}
            disabled={isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
};
